import { uuid } from 'lib/utils/dom'

import { LogsQuery } from '~/queries/schema/schema-general'

import { AttributeColumnConfig, ParsedLogMessage } from 'products/logs/frontend/types'

export type LogsColumnType =
    | 'timestamp'
    | 'level'
    | 'source'
    | 'trace_id'
    | 'span_id'
    | 'pattern'
    | 'message'
    | 'custom'

export interface LogsColumnConfig {
    /** Stable identity for list operations (React keys, reorder). Never sent to the server. */
    id: string
    type: LogsColumnType
    /** Header label override. Defaults to the registry label (built-ins) or the expression (custom). */
    name?: string
    /**
     * Only meaningful for `type: 'custom'`: a source-prefixed shorthand (`attributes.<key>`,
     * `resource_attributes.<key>`, `body.<json.path>`) or a scalar HogQL expression, sent
     * verbatim in `LogsQuery.customColumns`. Column order is the array index.
     */
    expression?: string
    width?: number
}

interface BuiltInColumnDef {
    label: string
    /** Reads the value off the row. Absent when the value only exists server-side (see `expression`). */
    getValue?: (log: ParsedLogMessage) => string
    /** HogQL the server computes for this column, for values a log row does not carry. */
    expression?: string
}

// Most built-in column types resolve client-side from fields every log row already carries —
// they never hit the wire. `source` has no top-level row field; the service name rides in
// resource_attributes per OTel convention. `pattern` is the exception: it is a table column the
// row payload leaves out, so it rides the same wire mechanism as custom columns and is only
// fetched while the column is on screen.
export const LOGS_COLUMN_REGISTRY: Record<Exclude<LogsColumnType, 'custom'>, BuiltInColumnDef> = {
    timestamp: { label: 'Timestamp', getValue: (log) => log.timestamp },
    level: { label: 'Level', getValue: (log) => log.severity_text },
    source: { label: 'Source', getValue: (log) => String(log.resource_attributes?.['service.name'] ?? '') },
    trace_id: { label: 'Trace ID', getValue: (log) => log.trace_id },
    span_id: { label: 'Span ID', getValue: (log) => log.span_id },
    pattern: { label: 'Pattern', expression: 'pattern' },
    message: { label: 'Message', getValue: (log) => log.body },
}

// Mirrors today's default table (timestamp + message) so the rendering cutover is invisible
// for users with no column customization.
export const DEFAULT_LOGS_COLUMNS: LogsColumnConfig[] = [
    { id: 'timestamp', type: 'timestamp' },
    { id: 'message', type: 'message' },
]

/**
 * Message is pinned to the end: it's the flex fill column, and the row FAB (whose scroll
 * buttons drive the message cell) anchors to the row's right edge.
 */
export function isPinnedColumn(column: LogsColumnConfig): boolean {
    return column.type === 'message'
}

/** Sort pinned columns last, stable otherwise. Returns the input untouched when already normalized. */
export function normalizeColumns(columns: LogsColumnConfig[]): LogsColumnConfig[] {
    const firstPinnedIndex = columns.findIndex(isPinnedColumn)
    if (firstPinnedIndex === -1 || columns.slice(firstPinnedIndex).every(isPinnedColumn)) {
        return columns
    }
    const rest = columns.filter((column) => !isPinnedColumn(column))
    return [...rest, ...columns.filter(isPinnedColumn)]
}

/** The one validation rule shared by the add-form and the draft: custom columns need an expression. */
export function customColumnExpressionError(type: LogsColumnType, expression: string | undefined): string | null {
    return type === 'custom' && !expression?.trim() ? 'Custom columns need an expression' : null
}

export function columnLabel(column: LogsColumnConfig): string {
    if (column.name) {
        return column.name
    }
    return column.type === 'custom' ? (column.expression ?? '') : LOGS_COLUMN_REGISTRY[column.type].label
}

/**
 * The HogQL the server must compute for a column, or undefined when the row already carries
 * the value. Keys the server's returned alias, so the wire list and the cell lookup stay in step.
 */
export function columnExpression(column: LogsColumnConfig): string | undefined {
    const expression = column.type === 'custom' ? column.expression : LOGS_COLUMN_REGISTRY[column.type].expression
    return expression?.trim() || undefined
}

/** Lower a column list to the `LogsQuery.customColumns` wire value. Client-side built-ins never hit the wire. */
export function columnsToCustomColumns(columns: LogsColumnConfig[]): LogsQuery['customColumns'] {
    // Deduplicated: the server aliases each expression by its hash, so sending one twice would
    // define the same alias twice and fail the whole query. Two columns sharing an expression
    // (two Pattern columns, or Pattern next to a hand-written `pattern`) read the same alias.
    const expressions = [
        ...new Set(columns.map(columnExpression).filter((expression): expression is string => !!expression)),
    ]
    // Undefined (not []) when there are no server-computed columns, so the query payload is
    // byte-identical to a pre-custom-columns query and cache keys are unaffected.
    return expressions.length > 0 ? expressions : undefined
}

function escapeHogQLString(value: string): string {
    return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")
}

/**
 * Expression preserving the legacy attribute-column lookup, which coalesced across both maps
 * (`log.attributes[key] ?? log.resource_attributes[key]`). The `attributes.<key>` shorthand
 * reads only one map, so a plain shorthand would render resource-attribute columns empty.
 */
export function attributeLookupExpression(key: string): string {
    const escaped = escapeHogQLString(key)
    return `if(mapContains(attributes, '${escaped}'), attributes['${escaped}'], resource_attributes['${escaped}'])`
}

/** Migrate the legacy persisted `attributeColumnsConfig` map to typed custom columns. */
export function migrateAttributeColumns(config: Record<string, AttributeColumnConfig>): LogsColumnConfig[] {
    return Object.entries(config)
        .sort(([, a], [, b]) => a.order - b.order)
        .map(([key, { width }]) => ({
            id: uuid(),
            type: 'custom' as const,
            name: key,
            expression: attributeLookupExpression(key),
            ...(width !== undefined ? { width } : {}),
        }))
}
